import styled from 'styled-components'

import { theme } from 'styles/theme'

import { fluidType } from '../fluid-type'

import { DESIGN_TEXT_RATIO } from './data'

const { colors, media, radii, space } = theme

export { Section } from 'components/general-styles/General'

export const TitleWrapper = styled.div`
  text-align: center;

  h2 {
    margin: 0;
    font-size: ${fluidType.sentenceTitle};
    letter-spacing: -0.02em;
  }
`

export const Steps = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.625rem;
  margin-top: ${space['spacing-xl']};

  @media (${media.desktop}) {
    margin-top: 3.125rem;
  }
`

/* A pílula ativa inverte: fundo escuro com borda ciano. As inativas ficam
   apenas contornadas, como no desenho. */
export const StepButton = styled.button<{ $active: boolean }>`
  all: unset;
  padding: ${space['spacing-xxs']} ${space['spacing-sm']};
  border: 1px solid ${({ $active }) => ($active ? colors['cyan-text-contrast'] : colors['neutral-border-pressed'])};
  border-radius: ${radii['corner-radius-lg']};
  background-color: ${({ $active }) => ($active ? colors['neutral-background-contrast'] : 'transparent')};
  color: ${({ $active }) => ($active ? colors['neutral-text-contrast-default'] : colors['neutral-text-default'])};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${colors['blue-background-strong-default']};
    outline-offset: 2px;
  }

  @media (${media.motion}: reduce) {
    transition: none;
  }
`

/* O eco azul do desenho é uma sombra sólida deslocada, não uma segunda caixa. */
export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};
  margin-top: ${space['spacing-xl']};
  padding: ${space['spacing-lg']} ${space['spacing-md']};
  background-color: ${colors['neutral-background-contrast']};
  border: 1px solid ${colors['blue-background-strong-default']};
  border-radius: 1.875rem;
  box-shadow: 10px 10px 0 0 ${colors['blue-background-strong-default']};

  @media (${media.desktop}) {
    gap: 3.125rem;
    margin-top: 3.125rem;
    padding: 3.125rem;
  }
`

export const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};

  @media (${media.desktop}) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`

export const PanelText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['spacing-xl']};

  h3,
  p {
    margin: 0;
  }

  h3 {
    font-size: ${fluidType.pillar};
    letter-spacing: -0.02em;
  }

  /* 653 dos 1243 de área útil no desenho. Era um teto de 41rem, que num card
     mais estreito que o do desenho virava 62% da linha em vez de 52%. */
  @media (${media.desktop}) {
    flex: 0 0 ${DESIGN_TEXT_RATIO * 100}%;
  }
`

/* Cada ilustração ocupa a fração que tem no desenho, sempre encostada na borda
   direita — de 23% (etapa 1) a 38% (etapa 3) da área útil. Renderizar no
   tamanho intrínseco do arquivo estourava a linha nas etapas maiores, porque
   nosso card é mais estreito que os 1343 do desenho. */
export const Illustration = styled.div<{ $ratio: number }>`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: auto;
  }

  @media (${media.desktop}) {
    flex: 0 0 ${({ $ratio }) => `${($ratio * 100).toFixed(2)}%`};
  }
`

export const PanelFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${space['spacing-lg']};

  /* O CTA vinha com o padrão cru do design system: raio corner-radius-sm,
     conteúdo em space-between e gap de 32px. No desenho é pílula de 398px,
     com rótulo e seta juntos no centro — o mesmo padrão dos outros CTAs. */
  .ds-button {
    justify-content: center;
    gap: ${space['spacing-xs']};
    border-radius: ${radii['corner-radius-pill']};
  }

  @media (${media.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .ds-button {
      width: 24.875rem;
    }
  }
`

export const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['spacing-nano']};
`

/* O ponto ativo vira uma pílula alongada, como no desenho. */
export const Dot = styled.button<{ $active: boolean }>`
  all: unset;
  width: ${({ $active }) => ($active ? '1.25rem' : '0.625rem')};
  height: 0.625rem;
  background-color: ${({ $active }) =>
    $active ? colors['cyan-background-pressed'] : colors['neutral-text-contrast-default']};
  border-radius: ${radii['corner-radius-pill']};
  cursor: pointer;
  transition: width 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${colors['cyan-background-pressed']};
    outline-offset: 2px;
  }

  @media (${media.motion}: reduce) {
    transition: none;
  }
`
